import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Brain, BookOpen, Calculator, Ruler, PenTool, Clock, MessageSquare, Search, Globe } from 'lucide-react';
import { useAppStore } from '../../hooks/useAppStore';
import { MODULES } from '../../data/modules';
import { GrowthProgress } from './GrowthProgress';
import { ModuleId, SubSkillProgress } from '../../types';
import { computeSkillMasteries, getActiveSkillCount } from '../../utils/skillAggregation';
export function SkillMasteryPanel() {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const [isOpen, setIsOpen] = useState(true);
  const [activeModule, setActiveModule] = useState<ModuleId | null>(null);
  // Get icon for module
  const getModuleIcon = (iconName: string) => {
    switch (iconName) {
      case 'Ruler':
        return <Ruler className="w-5 h-5" data-id="element-500" />;
      case 'Calculator':
        return <Calculator className="w-5 h-5" data-id="element-501" />;
      case 'Clock':
        return <Clock className="w-5 h-5" data-id="element-502" />;
      case 'PenTool':
        return <PenTool className="w-5 h-5" data-id="element-503" />;
      case 'BookOpen':
        return <BookOpen className="w-5 h-5" data-id="element-504" />;
      case 'MessageSquare':
        return <MessageSquare className="w-5 h-5" data-id="element-505" />;
      case 'Search':
        return <Search className="w-5 h-5" data-id="element-506" />;
      case 'Globe':
        return <Globe className="w-5 h-5" data-id="element-507" />;
      default:
        return <Brain className="w-5 h-5" data-id="element-508" />;
    }
  };
  /**
   * Build a subSkills record from aggregated skill data for GrowthProgress/LevelLadder.
   * GrowthProgress still expects Record<string, SubSkillProgress> — we pass the
   * underlying subSkill data through so the LevelLadder continues to work correctly.
   */
  const getGroupAwareSubSkills = useMemo(() => {
    return (moduleId: ModuleId): Record<string, SubSkillProgress> => {
      const skillMasteries = computeSkillMasteries(moduleId, selectedGroup, state.questionStates);
      const result: Record<string, SubSkillProgress> = {};
      skillMasteries.forEach(sm => {
        sm.subSkills.forEach(sub => {
          if (sub.questionsAnswered > 0) {
            result[sub.subSkill] = sub;
          }
        });
      });
      return result;
    };
  }, [state.questionStates, selectedGroup]);
  // Count active skills per module (now at skill level, not subSkill)
  const getActiveSkills = (moduleId: ModuleId): number => {
    return getActiveSkillCount(moduleId, selectedGroup, state.questionStates);
  };
  return <div className="mb-12" data-id="element-509">
      <div className="flex items-center justify-between mb-4" data-id="element-510">
        <div className="flex items-center gap-3" data-id="element-511">
          <div className="bg-indigo-100 p-2 rounded-lg" data-id="element-512">
            <Brain className="w-6 h-6 text-indigo-600" data-id="element-513" />
          </div>
          <div data-id="element-514">
            <h2 className="text-xl font-bold text-slate-900" data-id="element-515">
              Jouw Groei & Ontwikkeling
            </h2>
            <p className="text-sm text-slate-600" data-id="element-516">
              Groep {selectedGroup} — Bekijk hoe je vaardigheden groeien
            </p>
          </div>
        </div>
        <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-indigo-600 transition-colors bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm" data-id="element-517">
          {isOpen ? 'Verbergen' : 'Tonen'}
          {isOpen ? <ChevronUp className="w-4 h-4" data-id="element-518" /> : <ChevronDown className="w-4 h-4" data-id="element-519" />}
        </button>
      </div>

      <AnimatePresence data-id="element-520">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-521">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-522">
              {/* Module Selector */}
              <div className="lg:col-span-1 space-y-3" data-id="element-523">
                {MODULES.map(module => {
              const activeSkills = getActiveSkills(module.id);
              const isActive = activeModule === module.id || activeModule === null && module.id === 'Getalbegrip';
              return <button key={module.id} onClick={() => setActiveModule(module.id)} className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${isActive ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-slate-100 bg-white hover:border-indigo-200 hover:bg-slate-50'}`} data-id="element-524">
                      <div className={`p-2 rounded-lg ${isActive ? 'bg-indigo-200 text-indigo-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-525">
                        {getModuleIcon(module.icon)}
                      </div>
                      <div className="flex-1" data-id="element-526">
                        <div className="font-bold text-slate-900" data-id="element-527">
                          {module.title}
                        </div>
                        <div className="text-xs text-slate-500 mt-1" data-id="element-528">
                          {activeSkills}{' '}
                          {activeSkills === 1 ? 'vaardigheid' : 'vaardigheden'}{' '}
                          actief
                        </div>
                      </div>
                    </button>;
            })}
              </div>

              {/* Growth View */}
              <div className="lg:col-span-3" data-id="element-529">
                <div className="h-full p-6 bg-white border border-slate-200 rounded-3xl shadow-sm" data-id="element-530">
                  {(() => {
                const currentModuleId = activeModule || 'Getalbegrip';
                const currentModule = MODULES.find(m => m.id === currentModuleId);
                const groupSubSkills = getGroupAwareSubSkills(currentModuleId);
                return <div className="space-y-6" data-id="element-531">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-4" data-id="element-532">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-533">
                            {currentModule?.title}
                          </h3>
                          <span className="text-xs font-medium text-slate-400 bg-slate-100 px-3 py-1 rounded-full" data-id="element-534">
                            Groep {selectedGroup}
                          </span>
                        </div>

                        <GrowthProgress moduleId={currentModuleId} subSkills={groupSubSkills} data-id="element-535" />
                      </div>;
              })()}
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}